import React from 'react';
import { Hotel } from '../../types/Quotation';
import { getDatePlusDays, parseDateValue } from '../../utils/date';

interface HotelCardProps {
  hotel: Hotel & { day: number };
  variant?: 'default' | 'inclusion';
  mealPlan?: string;
  selectedRoomCount?: number;
  travelStartDate?: string;
}

const formatStayDate = (value?: string) => {
  if (!value) return 'TBD';
  const date = parseDateValue(value);
  if (!date || Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatMealType = (value?: string) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const normalized = raw.toUpperCase().replace(/[_\s]+/g, '');
  if (normalized === 'CP' || normalized === 'CPAI') return 'Breakfast only';
  if (normalized === 'MAPI') return 'Breakfast + Lunch / Dinner';
  if (normalized === 'API') return 'Breakfast + Lunch + Dinner';

  return raw.replace(/[_]+/g, ' ');
};

const HotelCard: React.FC<HotelCardProps> = ({ hotel, variant = 'default', mealPlan, selectedRoomCount, travelStartDate }) => {
  const anyHotel = hotel as any;

  const renderStars = (rating: string) => {
    const stars = parseInt(rating) || 0;
    return '★'.repeat(stars) + '☆'.repeat(5 - stars);
  };

  const resolveAllImages = (): string[] => {
    const roomVariantImages = Array.isArray(hotel.roomVariant?.images) ? hotel.roomVariant.images : [];
    const roomVariantGallery = Array.isArray(hotel.roomVariant?.gallery) ? hotel.roomVariant.gallery : [];
    const images = Array.isArray(anyHotel.images) ? anyHotel.images : [];
    const gallery = Array.isArray(anyHotel.gallery) ? anyHotel.gallery : [];
    return Array.from(
      new Set(
        [...roomVariantImages, ...roomVariantGallery, ...images, ...gallery]
          .map((image) => String(image || '').trim())
          .filter(Boolean)
      )
    );
  };

  const resolveHotelImage = () => {
    const direct = hotel.thumbnail || anyHotel.image || anyHotel.imageUrl || anyHotel.image_url;
    if (direct) return direct;
    const allImages = resolveAllImages();
    if (allImages.length > 0) return allImages[0];
    return '';
  };

  const primaryImage = resolveHotelImage();
  const galleryImages = resolveAllImages()
    .filter((image) => image !== primaryImage)
    .slice(0, 3);
  const mealType =
    formatMealType(
      hotel.roomVariant?.boardBasis ||
        anyHotel.meal_type ||
        anyHotel.mealType ||
        anyHotel.boardBasis ||
        anyHotel.board_basis ||
        mealPlan
    );
  const showInclusionStayDetails = variant === 'inclusion';
  const fallbackCheckInDate = travelStartDate
    ? getDatePlusDays(travelStartDate, Math.max(0, Number(hotel.day || 1) - 1))
    : '';
  const effectiveCheckInDate = hotel.checkInDate || fallbackCheckInDate;

  const getAmenityIcon = (amenity: string) => {
    const amenityLower = amenity.toLowerCase();
    if (amenityLower.includes('wifi') || amenityLower.includes('internet')) return '📶';
    if (amenityLower.includes('pool') || amenityLower.includes('swimming')) return '🏊';
    if (amenityLower.includes('gym') || amenityLower.includes('fitness')) return '💪';
    if (amenityLower.includes('spa') || amenityLower.includes('wellness')) return '🧘';
    if (amenityLower.includes('restaurant') || amenityLower.includes('dining')) return '🍽️';
    if (amenityLower.includes('parking')) return '🅿️';
    if (amenityLower.includes('airport') || amenityLower.includes('shuttle')) return '🚌';
    if (amenityLower.includes('concierge') || amenityLower.includes('service')) return '🛎️';
    if (amenityLower.includes('bar') || amenityLower.includes('lounge')) return '🍸';
    if (amenityLower.includes('business') || amenityLower.includes('meeting')) return '💼';
    return '✨';
  };

  return (
    <div style={{
      border: '1px solid #e1e5e9',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px',
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      display: 'flex',
      gap: '16px'
    }}>
      {/* Hotel Images */}
      <div style={{ flex: '0 0 120px' }}>
        {/* Main Thumbnail */}
        <div style={{
          width: '100%',
          height: '80px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6c757d',
          fontSize: '24px',
          marginBottom: '8px'
        }}>
          {primaryImage ? (
            <img
              src={primaryImage}
              alt={hotel.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px'
              }}
              loading="eager"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          ) : (
            '🏨'
          )}
        </div>

        {/* Image Gallery */}
        {galleryImages.length > 0 && (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {galleryImages.map((imageUrl: string, index: number) => (
              <div
                key={index}
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img
                  src={imageUrl}
                  alt={`${hotel.name} gallery ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div style={{
                  display: 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#e9ecef',
                  color: '#6c757d',
                  fontSize: '12px'
                }}>
                  🖼️
                </div>
              </div>
            ))}
            </div>
        )}
      </div>

      {/* Hotel Details */}
      <div style={{ flex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
              {hotel.name}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <span style={{ color: '#f39c12', fontSize: '14px' }}>
                {renderStars(hotel.starRating)}
              </span>
              <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
                {hotel.starRating} stars
              </span>
            </div>
          </div>
        </div>

        {/* Address */}
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
            📍 {hotel.address}
          </span>
        </div>

        {/* Room Details */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', flexWrap: 'wrap' }}>
          {hotel.roomType && (
            <div style={{ fontSize: '12px', color: '#34495e' }}>
              <strong>Room:</strong> {hotel.roomType}
            </div>
          )}
          {mealType && mealType !== 'None' && (
            <div style={{ fontSize: '12px', color: '#34495e' }}>
              <strong>Meal type:</strong> {mealType}
            </div>
          )}
          <div style={{ fontSize: '12px', color: '#34495e' }}>
              <strong>Rooms:</strong> {typeof selectedRoomCount === 'number' ? `${selectedRoomCount}` : 'Not specified'}
          </div>
          {showInclusionStayDetails && mealPlan && mealPlan !== 'None' && (
            <div style={{ fontSize: '12px', color: '#34495e' }}>
              <strong>Meal plan:</strong> {mealPlan}
            </div>
          )}
        </div>

        {/* Amenities */}
        {hotel.amenities && hotel.amenities.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>
              <strong>Amenities:</strong>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {hotel.amenities.slice(0, 6).map((amenity, index) => (
                <span key={index} style={{ 
                  fontSize: '12px', 
                  color: '#34495e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px'
                }}>
                  {getAmenityIcon(amenity)} {amenity}
                </span>
              ))}
              {hotel.amenities.length > 6 && (
                <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
                  +{hotel.amenities.length - 6} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Stay Details */}
        <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
          {showInclusionStayDetails && (
            <>
              <div><strong>Check-in date:</strong> {formatStayDate(effectiveCheckInDate)}</div>
              <div><strong>Check-out date:</strong> {formatStayDate(hotel.checkOutDate)}</div>
            </>
          )}
          <div>{showInclusionStayDetails ? 'Standard check-in time' : 'Check-in'}: {hotel.checkin_time}</div>
          <div>{showInclusionStayDetails ? 'Standard check-out time' : 'Check-out'}: {hotel.checkout_time}</div>
        </div>
      </div>
    </div>
  );
};

export default HotelCard;
